import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { DjangoWaffleType } from "./django-waffle-type";

// allow window.waffle to hydrate us
declare global {
  interface Window {
    waffle: Partial<DjangoWaffleType>;
  }
}

// fallback until the real data arrives
const DEFAULT_WAFFLE: DjangoWaffleType = {
  FLAGS: {},
  SWITCHES: {},
  SAMPLES: {},
  FLAG_VENUE_IDS: {},
  FLAG_INCLUDED_VENUE_IDS: {},
  FLAG_EXCLUDED_VENUE_IDS: {},
  flag_is_active: () => false,
};

const WaffleContext = createContext<DjangoWaffleType>(DEFAULT_WAFFLE);

export const WaffleProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [waffle, setWaffle] = useState<DjangoWaffleType>(DEFAULT_WAFFLE);

  useEffect(() => {
    if (typeof window !== "undefined" && window.waffle) {
      setWaffle({
        FLAGS: window.waffle.FLAGS ?? {},
        SWITCHES: window.waffle.SWITCHES ?? {},
        SAMPLES: window.waffle.SAMPLES ?? {},
        FLAG_VENUE_IDS: window.waffle.FLAG_VENUE_IDS ?? {},
        FLAG_INCLUDED_VENUE_IDS: window.waffle.FLAG_INCLUDED_VENUE_IDS ?? {},
        FLAG_EXCLUDED_VENUE_IDS: window.waffle.FLAG_EXCLUDED_VENUE_IDS ?? {},
        flag_is_active: window.waffle.flag_is_active ?? (() => false),
      });
    }
  }, []);

  return (
    <WaffleContext.Provider value={waffle}>{children}</WaffleContext.Provider>
  );
};

export type WaffleContextValue = DjangoWaffleType & {
  /**
   * Determine if a feature flag is active, with venue overrides taking priority:
   * 1. If venue is explicitly excluded in flag config, flag is always disabled
   * 2. If venue is explicitly included in flag config, check the base flag status
   * 3. If flag has venue scoping but current venue isn't included in flag config, disable the flag
   * 4. If flag has no venue scoping, check the base flag status
   */
  isFlagActive: (flagName: string, venueId?: number) => boolean;
};

/**
 * Checks if a feature flag is active, taking into account venue-specific scoping rules.
 *
 * @param waffle - The Django Waffle configuration object
 * @param flagName - The name of the feature flag to check
 * @param venueId - Optional venue ID for venue-scoped flag checks
 * @returns boolean indicating if the flag is active for the given context
 */
export const checkFlagActive = (
  waffle: DjangoWaffleType,
  flagName: string,
  venueId?: number
) => {
  const {
    flag_is_active,
    FLAG_VENUE_IDS,
    FLAG_INCLUDED_VENUE_IDS,
    FLAG_EXCLUDED_VENUE_IDS,
  } = waffle;

  // Get venue lists specific to this flag
  const scopedVenues = FLAG_VENUE_IDS?.[flagName] || [];
  const scopedIncludedVenues = FLAG_INCLUDED_VENUE_IDS?.[flagName] || [];
  const scopedExcludedVenues = FLAG_EXCLUDED_VENUE_IDS?.[flagName] || [];

  // Determine venue inclusion/exclusion status
  const venueIsPercentEligible =
    venueId !== undefined && scopedVenues.includes(venueId);
  const venueIsIncluded =
    venueId !== undefined && scopedIncludedVenues.includes(venueId);
  const venueIsExcluded =
    venueId !== undefined && scopedExcludedVenues.includes(venueId);

  // Priority 1: If venue is explicitly excluded in flag config, flag is always disabled
  // This is prioritized to err on the side of disabling the flag for safety
  if (venueIsExcluded) {
    return false;
  }

  // Priority 2: If venue is explicitly included in flag config, flag is always enabled
  if (venueIsIncluded) {
    return true;
  }

  // Priority 3: If venue is explicitly included in flag config, check the base flag status
  if (venueIsPercentEligible) {
    return flag_is_active(flagName);
  }

  // Priority 4: If flag has venue scoping but current venue isn't eligible in flag config, disable the flag
  if (scopedVenues.length > 0) {
    return false;
  }

  // Priority 5: No venue scoping restrictions, use base flag status
  return flag_is_active(flagName);
};

/**
 * React hook to read waffle data and do venue-aware flag checks.
 * Must be used under <WaffleProvider>.
 */
export function useWaffle(): WaffleContextValue {
  const waffle = useContext(WaffleContext);
  if (!waffle) {
    throw new Error("useWaffle must be used within a WaffleProvider");
  }

  const isFlagActive = useCallback(
    (flagName: string, venueId?: number): boolean => {
      return checkFlagActive(waffle, flagName, venueId);
    },
    [waffle]
  );

  return { ...waffle, isFlagActive };
}
